import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EventTypes,
  ICardPaymentMethodManager,
  PaymentMethodType,
  PrimerHeadlessCheckout,
} from '@primer-io/checkout-web'

import {
  PRIMER_CARD_CVC_ID,
  PRIMER_CARD_EXPIRY_ID,
  PRIMER_CARD_NUMBER_ID,
  PRIMER_FORM_COMMON_STYLES,
  PrimerErrorCode,
} from 'modules/purchase/constants'
import { getDefaultPaymentErrorsState } from 'modules/purchase/helpers/rootHelpers'
import { TPaymentErrorState } from 'modules/purchase/types'

import { CardPaymentFieldName } from 'root-constants'

export const useInitPrimerCardForm = ({
  primerRef,
  shouldAutofocus = true,
}: {
  primerRef: React.RefObject<PrimerHeadlessCheckout | null>
  shouldAutofocus?: boolean
}): {
  initCardForm: () => Promise<void>
  cardManagerRef: React.RefObject<ICardPaymentMethodManager | null>
  errors: TPaymentErrorState
  isFormValid: boolean
  handleChange: (eventValue: {
    fieldName: CardPaymentFieldName
    hasError: boolean
    isComplete: boolean
    isFocused: boolean
  }) => void
} => {
  const { t } = useTranslation()
  const cardManagerRef = useRef<ICardPaymentMethodManager | null>(null)
  const [errors, setErrors] = useState<TPaymentErrorState>(() =>
    getDefaultPaymentErrorsState(),
  )

  const hasErrors = useMemo(
    () => Object.values(errors).some(({ error }) => !!error),
    [errors],
  )
  const hasUntouchedFields = useMemo(
    () => Object.values(errors).some(({ isTouched }) => !isTouched),
    [errors],
  )
  const hasUncompletedFields = useMemo(
    () => Object.values(errors).some(({ isComplete }) => !isComplete),
    [errors],
  )
  const isFormValid = useMemo(
    () => !hasErrors && !hasUntouchedFields && !hasUncompletedFields,
    [hasErrors, hasUncompletedFields, hasUntouchedFields],
  )

  const handleChange = useCallback(
    ({
      fieldName,
      hasError,
      isComplete,
      isFocused,
    }: {
      fieldName: CardPaymentFieldName
      hasError: boolean
      isComplete: boolean
      isFocused: boolean
    }) => {
      let error = ''

      if (hasError) {
        error = PrimerErrorCode.CARD_IS_INVALID
      }

      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: {
          isTouched: true,
          error,
          isComplete,
          isShown: true,
          isFocused,
        },
      }))
    },
    [],
  )

  const initCardForm = useCallback(async () => {
    if (!primerRef.current) return
    const cardManager = await primerRef.current?.createPaymentMethodManager(
      PaymentMethodType.PAYMENT_CARD,
    )

    if (!cardManager) return

    const { cardNumberInput, expiryInput, cvvInput } =
      cardManager.createHostedInputs()

    await cardManager.validate()
    await Promise.all([
      cardNumberInput.render(PRIMER_CARD_NUMBER_ID, {
        placeholder: '1234 1234 1234 1234',
        style: PRIMER_FORM_COMMON_STYLES,
      }),
      expiryInput.render(PRIMER_CARD_EXPIRY_ID, {
        placeholder: t('purchase1.paymentForm.cardExpiryPlaceholder'),
        style: PRIMER_FORM_COMMON_STYLES,
      }),
      cvvInput.render(PRIMER_CARD_CVC_ID, {
        placeholder: 'CVC',
        style: PRIMER_FORM_COMMON_STYLES,
      }),
    ])

    if (shouldAutofocus) {
      cardNumberInput.focus()
    }

    cardNumberInput.addEventListener(EventTypes.CHANGE, (event: any) => {
      handleChange({
        fieldName: CardPaymentFieldName.NUMBER,
        hasError:
          !event.valid && event.errorCode !== PrimerErrorCode.CARD_REQUIRED,
        isComplete: event.valid,
        isFocused: event.active,
      })
    })

    expiryInput.addEventListener(EventTypes.CHANGE, (event: any) => {
      handleChange({
        fieldName: CardPaymentFieldName.EXPIRY,
        hasError:
          !event.valid && event.errorCode !== PrimerErrorCode.EXPIRY_REQUIRED,
        isComplete: event.valid,
        isFocused: event.active,
      })
    })

    cvvInput.addEventListener(EventTypes.CHANGE, (event: any) => {
      handleChange({
        fieldName: CardPaymentFieldName.CVC,
        hasError:
          !event.valid &&
          (event.errorCode === PrimerErrorCode.CVV_IS_INCOMPLETE ||
            event.errorCode === PrimerErrorCode.CVV_IS_INVALID),
        isComplete: event.valid,
        isFocused: event.active,
      })
    })

    cardManagerRef.current = cardManager
  }, [handleChange, primerRef, shouldAutofocus, t])

  return {
    initCardForm,
    cardManagerRef,
    errors,
    isFormValid,
    handleChange,
  }
}
